/**
 * Push Notifications Service for Capacitor (iOS/Android)
 *
 * Handles:
 * - Native push notification registration
 * - Permission requests on mobile
 * - FCM token management for native apps
 */

import { Capacitor, registerPlugin, PluginListenerHandle } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';
import { clearStaleTokens } from '@/services/notificationService';

// Define the interface for our custom FCM plugin
interface FCMPluginInterface {
  setUserId(options: { userId: string }): Promise<{ success: boolean; message: string }>;
  getToken(): Promise<{ token: string | null }>;
}

// Register the custom FCM plugin (only available on iOS)
const FCMPlugin = registerPlugin<FCMPluginInterface>('FCMPlugin');

// Module-level listener state.
// - Tap/receive listeners must be registered ONCE and never removed, otherwise
//   notification taps are silently dropped (this was the deep-link bug).
// - Registration listeners are replaced on each register() call via their handles,
//   instead of PushNotifications.removeAllListeners() which also killed the tap listener.
let tapListenersRegistered = false;
let registrationHandles: PluginListenerHandle[] = [];

/** Remove only the transient 'registration'/'registrationError' listeners */
const clearRegistrationListeners = async (): Promise<void> => {
  const handles = registrationHandles;
  registrationHandles = [];
  for (const handle of handles) {
    try {
      await handle.remove();
    } catch {
      // Ignore - handle may already be removed
    }
  }
};

/** Track a registration listener handle (addListener returns a promise) */
const trackRegistrationListener = (
  handlePromise: Promise<PluginListenerHandle> | PluginListenerHandle
): void => {
  Promise.resolve(handlePromise)
    .then((handle) => registrationHandles.push(handle))
    .catch(() => undefined);
};

// Check if we're running on a native platform
export const isNativePlatform = (): boolean => {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();
  console.log(`[PushNotifications] Platform check: isNative=${isNative}, platform=${platform}`);
  return isNative;
};

// Check if push notifications are available
export const isPushNotificationsAvailable = (): boolean => {
  const isNative = isNativePlatform();
  const pluginAvailable = Capacitor.isPluginAvailable('PushNotifications');
  console.log(`[PushNotifications] Availability check: isNative=${isNative}, pluginAvailable=${pluginAvailable}`);
  return isNative && pluginAvailable;
};

/**
 * Request permission for push notifications on native platforms
 */
export const requestNativePushPermission = async (): Promise<'granted' | 'denied' | 'prompt'> => {
  if (!isPushNotificationsAvailable()) {
    console.warn('Push notifications not available on this platform');
    return 'denied';
  }

  try {
    const permissionStatus = await PushNotifications.checkPermissions();
    console.log('Current permission status:', permissionStatus);

    if (permissionStatus.receive === 'granted') {
      return 'granted';
    }

    if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
      const requestResult = await PushNotifications.requestPermissions();
      console.log('Permission request result:', requestResult);

      if (requestResult.receive === 'granted') {
        return 'granted';
      }
      return 'denied';
    }

    return 'denied';
  } catch (error) {
    console.error('Error requesting push permission:', error);
    return 'denied';
  }
};

/**
 * Register for push notifications and get FCM token
 */
export const registerNativePushNotifications = async (): Promise<string | null> => {
  console.log('[PushNotifications] Starting registration...');
  console.log('[PushNotifications] Current auth state:', auth.currentUser?.uid || 'NOT AUTHENTICATED');

  if (!isPushNotificationsAvailable()) {
    console.warn('[PushNotifications] Push notifications not available');
    return null;
  }

  try {
    // Request permission first
    console.log('[PushNotifications] Requesting permission...');
    const permission = await requestNativePushPermission();
    console.log('[PushNotifications] Permission result:', permission);

    if (permission !== 'granted') {
      console.warn('[PushNotifications] Push notification permission not granted');
      return null;
    }

    // Make sure the tap/receive listeners exist (idempotent) and remove ONLY
    // previous registration listeners. Never use removeAllListeners() here:
    // it destroys the pushNotificationActionPerformed listener and breaks
    // notification tap navigation.
    setupPushNotificationListeners();
    await clearRegistrationListeners();
    console.log('[PushNotifications] Cleared previous registration listeners');

    // Register for push notifications
    console.log('[PushNotifications] Calling PushNotifications.register()...');
    await PushNotifications.register();
    console.log('[PushNotifications] Register called, waiting for token...');

    // Return a promise that resolves with the token
    return new Promise((resolve) => {
      // Set a timeout in case registration fails silently
      const timeout = setTimeout(() => {
        console.warn('[PushNotifications] Push registration timeout after 15s');
        resolve(null);
      }, 15000); // Increased to 15s for slower connections

      // Listen for registration success
      trackRegistrationListener(PushNotifications.addListener('registration', async (token: Token) => {
        clearTimeout(timeout);
        console.log('[PushNotifications] ✅ Token received from APNs/FCM');
        console.log('[PushNotifications] Token value:', token.value?.substring(0, 40) + '...');
        console.log('[PushNotifications] Token length:', token.value?.length);

        if (!token.value) {
          console.error('[PushNotifications] ❌ Token value is empty!');
          resolve(null);
          return;
        }

        // Save token to user profile with retry logic
        console.log('[PushNotifications] Attempting to save token to Firestore...');
        const saved = await saveNativeFCMToken(token.value);

        if (saved) {
          console.log('[PushNotifications] ✅ Token successfully saved to Firestore');
        } else {
          console.error('[PushNotifications] ❌ Failed to save token to Firestore');
          // Still resolve with token - the user can still receive notifications
          // if we can save the token later
        }

        resolve(token.value);
      }));

      // Listen for registration errors
      trackRegistrationListener(PushNotifications.addListener('registrationError', (error: any) => {
        clearTimeout(timeout);
        console.error('[PushNotifications] ❌ Registration error:', JSON.stringify(error));
        console.error('[PushNotifications] Error details:', error?.error || error?.message || 'Unknown error');
        resolve(null);
      }));
    });
  } catch (error: any) {
    console.error('[PushNotifications] ❌ Error registering:', error);
    console.error('[PushNotifications] Error message:', error?.message);
    console.error('[PushNotifications] Error stack:', error?.stack);
    return null;
  }
};

/**
 * Save FCM token via native iOS plugin (calls HTTP Cloud Function)
 * This is a backup mechanism that works even if JavaScript Firestore fails
 */
export const saveTokenViaNativePlugin = async (userId: string): Promise<boolean> => {
  const platform = Capacitor.getPlatform();

  // Only available on iOS for now
  if (platform !== 'ios') {
    console.log('[PushNotifications] Native FCM plugin only available on iOS');
    return false;
  }

  try {
    console.log('[PushNotifications] Calling native FCM plugin to save token...');
    const result = await FCMPlugin.setUserId({ userId });
    console.log('[PushNotifications] Native plugin result:', result);
    return result.success;
  } catch (error: any) {
    console.error('[PushNotifications] Error calling native FCM plugin:', error);
    return false;
  }
};

/**
 * Save FCM token to user's Firestore profile
 * Includes retry logic and better error handling for iOS
 * Also triggers native plugin as backup
 */
export const saveNativeFCMToken = async (token: string, retryCount = 0): Promise<boolean> => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 second

  console.log(`[PushNotifications] saveNativeFCMToken called, attempt ${retryCount + 1}/${MAX_RETRIES + 1}`);
  console.log(`[PushNotifications] Token to save: ${token?.substring(0, 30)}...`);

  const user = auth.currentUser;

  if (!user) {
    console.warn('[PushNotifications] ⚠️ No authenticated user to save FCM token');

    // Retry if we haven't exceeded max retries - auth might not be ready yet
    if (retryCount < MAX_RETRIES) {
      console.log(`[PushNotifications] Retrying in ${RETRY_DELAY}ms...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return saveNativeFCMToken(token, retryCount + 1);
    }

    console.error('[PushNotifications] ❌ Max retries reached, user still not authenticated');
    return false;
  }

  console.log(`[PushNotifications] User authenticated: ${user.uid}`);
  console.log(`[PushNotifications] User email: ${user.email}`);
  console.log(`[PushNotifications] Platform: ${Capacitor.getPlatform()}`);

  // Try both methods in parallel for redundancy
  let firestoreSaved = false;
  let nativeSaved = false;

  // Method 1: JavaScript Firestore
  try {
    const userRef = doc(db, 'users', user.uid);
    const dataToSave = {
      fcmToken: token,
      fcmTokenUpdatedAt: Timestamp.now(),
      notificationsEnabled: true,
      fcmTokenPlatform: Capacitor.getPlatform(), // 'android' or 'ios'
      fcmTokenLastSaved: new Date().toISOString() // Debug timestamp
    };

    console.log('[PushNotifications] Saving to Firestore via JS:', JSON.stringify(dataToSave, null, 2));

    await setDoc(userRef, dataToSave, { merge: true });

    // Also persist in the fcmTokens subcollection so the native token survives
    // even if a web session later overwrites the top-level fcmToken field.
    // Cloud Functions send to every token found in both places.
    try {
      const tokenRef = doc(db, 'users', user.uid, 'fcmTokens', token);
      await setDoc(tokenRef, {
        token,
        device: {
          platform: Capacitor.getPlatform(),
          native: true
        },
        createdAt: Timestamp.now(),
        lastUsedAt: Timestamp.now()
      }, { merge: true });
    } catch (subError) {
      console.warn('[PushNotifications] Could not save token to subcollection:', subError);
    }

    console.log('[PushNotifications] ✅ Native FCM token saved via JavaScript Firestore');
    console.log(`[PushNotifications] ✅ Saved to: users/${user.uid}`);
    firestoreSaved = true;
    // Clear this token from any other users (shared device)
    clearStaleTokens(user.uid, token);
  } catch (error: any) {
    console.error('[PushNotifications] ❌ Error saving FCM token via JS Firestore:', error);
    console.error('[PushNotifications] Error code:', error?.code);
    console.error('[PushNotifications] Error message:', error?.message);
  }

  // Method 2: Native plugin (iOS only) - calls HTTP Cloud Function
  if (Capacitor.getPlatform() === 'ios') {
    try {
      console.log('[PushNotifications] Also trying native plugin (HTTP backup)...');
      nativeSaved = await saveTokenViaNativePlugin(user.uid);
      if (nativeSaved) {
        console.log('[PushNotifications] ✅ Native FCM token saved via native plugin (HTTP)');
      }
    } catch (error: any) {
      console.error('[PushNotifications] ❌ Error saving via native plugin:', error);
    }
  }

  // Success if either method worked
  if (firestoreSaved || nativeSaved) {
    console.log(`[PushNotifications] ✅ Token saved (Firestore: ${firestoreSaved}, Native: ${nativeSaved})`);
    return true;
  }

  // Retry on transient errors
  if (retryCount < MAX_RETRIES) {
    console.log(`[PushNotifications] Retrying in ${RETRY_DELAY}ms...`);
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    return saveNativeFCMToken(token, retryCount + 1);
  }

  console.error('[PushNotifications] ❌ Failed to save token via any method');
  return false;
};

/**
 * Setup push notification listeners for the app
 */
export const setupPushNotificationListeners = (): (() => void) | null => {
  if (!isPushNotificationsAvailable()) {
    return null;
  }

  // Idempotent: these listeners must live for the whole app session.
  // Registering them twice would cause double navigation on tap.
  if (tapListenersRegistered) {
    return () => undefined;
  }
  tapListenersRegistered = true;

  // Listener for when a notification is received while app is in foreground
  PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      console.log('📨 Push notification received in foreground:', notification);
      // You can show an in-app notification here
    }
  );

  // Listener for when user taps on a notification
  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      console.log('🔔 Push notification action performed:', action);
      // Handle notification tap - navigate to relevant screen
      const data = action.notification.data;

      // Preferred: backend now sends the destination route in data.route
      const route = typeof data?.route === 'string' ? data.route : null;
      if (route && route.startsWith('/')) {
        window.location.href = route;
        return;
      }

      // Fallback by type (older payloads without route)
      const type = data?.type;
      if (
        type === 'your_turn' ||
        type === 'ticket_confirmed' ||
        type === 'ticket_created' ||
        type === 'position_update' ||
        type === 'appointment_converted_to_ticket'
      ) {
        window.location.href = '/client-queue';
      } else if (
        type === 'appointment_created' ||
        type === 'appointment_confirmed' ||
        type === 'appointment_cancelled'
      ) {
        window.location.href = data?.appointmentId
          ? `/appointments/${data.appointmentId}`
          : '/appointments';
      }
    }
  );

  // Return cleanup function.
  // NOTE: intentionally does NOT remove listeners — removeAllListeners() here
  // was the root cause of notification taps doing nothing (it killed the tap
  // listener as soon as token registration ran).
  return () => undefined;
};

/**
 * Initialize native push notifications
 * Call this after user logs in
 */
export const initializeNativePush = async (): Promise<string | null> => {
  console.log('[PushNotifications] initializeNativePush called');
  console.log('[PushNotifications] Platform:', Capacitor.getPlatform());
  console.log('[PushNotifications] isNative:', Capacitor.isNativePlatform());
  console.log('[PushNotifications] PushNotifications available:', Capacitor.isPluginAvailable('PushNotifications'));

  // For iOS, try to get token from native AppDelegate via UserDefaults
  if (Capacitor.getPlatform() === 'ios') {
    console.log('[PushNotifications] iOS detected, trying to get token from native...');

    // Try the custom FCM plugin first to get the stored token
    try {
      const result = await FCMPlugin.getToken();
      if (result.token) {
        console.log('[PushNotifications] Got token from native FCMPlugin:', result.token.substring(0, 30) + '...');
        return result.token;
      }
    } catch (e) {
      console.log('[PushNotifications] FCMPlugin.getToken failed:', e);
    }
  }

  if (!isPushNotificationsAvailable()) {
    console.log('[PushNotifications] Not a native platform or push not available, skipping native push init');
    return null;
  }

  console.log('[PushNotifications] Initializing native push notifications...');
  console.log('[PushNotifications] Auth state:', auth.currentUser?.uid || 'NOT AUTHENTICATED');

  // Setup listeners
  setupPushNotificationListeners();

  // Register and get token
  const token = await registerNativePushNotifications();

  if (token) {
    console.log('[PushNotifications] ✅ Native push notifications initialized successfully');
    console.log('[PushNotifications] Token obtained:', token.substring(0, 30) + '...');
  } else {
    console.warn('[PushNotifications] ⚠️ Failed to initialize native push notifications');
  }

  return token;
};

/**
 * Ensure FCM token is saved for the current user
 * Call this when you're sure the user is authenticated
 * Useful for re-syncing tokens that might not have been saved initially
 */
export const ensureFCMTokenSaved = async (): Promise<boolean> => {
  console.log('[PushNotifications] ensureFCMTokenSaved called');

  if (!isPushNotificationsAvailable()) {
    console.log('[PushNotifications] Not a native platform, skipping');
    return false;
  }

  const user = auth.currentUser;
  if (!user) {
    console.warn('[PushNotifications] No authenticated user');
    return false;
  }

  console.log('[PushNotifications] Checking if user has FCM token...');

  try {
    // Check permission status
    const permStatus = await PushNotifications.checkPermissions();
    console.log('[PushNotifications] Permission status:', permStatus.receive);

    if (permStatus.receive !== 'granted') {
      console.log('[PushNotifications] Notifications not granted, requesting...');
      const token = await registerNativePushNotifications();
      return !!token;
    }

    // Permission granted, try to get the current token and save it
    console.log('[PushNotifications] Permission granted, re-registering to get token...');

    // Re-register to get the token. Keep tap listeners alive and clear only
    // previous registration listeners (never removeAllListeners()).
    setupPushNotificationListeners();
    await clearRegistrationListeners();

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[PushNotifications] ensureFCMTokenSaved timeout');
        resolve(false);
      }, 10000);

      trackRegistrationListener(PushNotifications.addListener('registration', async (token: Token) => {
        clearTimeout(timeout);
        console.log('[PushNotifications] Got token in ensureFCMTokenSaved:', token.value?.substring(0, 30) + '...');

        if (token.value) {
          const saved = await saveNativeFCMToken(token.value);
          console.log('[PushNotifications] Token saved in ensureFCMTokenSaved:', saved);
          resolve(saved);
        } else {
          resolve(false);
        }
      }));

      trackRegistrationListener(PushNotifications.addListener('registrationError', (error: any) => {
        clearTimeout(timeout);
        console.error('[PushNotifications] Registration error in ensureFCMTokenSaved:', error);
        resolve(false);
      }));

      // Trigger registration
      PushNotifications.register();
    });
  } catch (error) {
    console.error('[PushNotifications] Error in ensureFCMTokenSaved:', error);
    return false;
  }
};
